'use client';
import React from 'react';

import { Carousel, CarouselRenderItemInfo } from '@/components/atoms/carousel';
import { CarouselItem } from '@/components/atoms/carousel/CarouselBlockItem';
import { NextImageProps } from '@/components/atoms/image';
import ImageSB, { ImageSBProps } from '@/components/atoms/image/ImageSB';
import {
  ToggleCMSProps,
  useCMSToggle,
} from '@/components/atoms/toggle/useCMSToggle';
import { BlockComponentModel } from '@/components/cms/types/blok-component';

import { BlockHeader, BlockHeaderContent } from '../utils/BlockHeaderContent';
import { BlockWrapper } from '../utils/BlockWrapper';
import { swiperConfig } from './carouselConfig';

export interface CarouselBlockCardStrapiProps {
  cardUrl?: string;
  cardTitle?: string;
  icon: ImageSBProps;
  actionLabel?: string;
  renderImage: (props: Partial<NextImageProps>) => React.ReactNode;
}

export interface CarouselBlockStrapiProps {
  cards: CarouselBlockCardStrapiProps[];
  header: BlockHeader;
  toggle?: ToggleCMSProps;
}

export const CarouselBlockStrapi = ({
  block,
  ...rest
}: BlockComponentModel<CarouselBlockStrapiProps>) => {
  const { toggle, header, cards } = block;
  const {
    filteredToggleList,
    toggleNode: ToggleNode,
    ToggleActionButton,
  } = useCMSToggle<typeof cards>(cards, toggle);
  const selectedCards = filteredToggleList || cards;

  const renderCarouselItem = ({
    item,
    index,
  }: CarouselRenderItemInfo<CarouselBlockCardStrapiProps>) => (
    <CarouselItem
      {...item}
      renderImage={(props) => (
        <ImageSB
          inner={{
            ...props,
            'data-tracker-context': `carousel-item-${index}`,
            width: '48',
            height: '48',
          }}
          block={item.icon}
        />
      )}
    />
  );

  return (
    <BlockWrapper {...rest} theme={header?.theme?.themeKey}>
      <div data-tracker-section='carousel-block'>
        <BlockHeaderContent
          {...header}
          topChildren={ToggleNode ? ToggleNode() : undefined}
          renderButtons={
            ToggleActionButton
              ? [
                  (props) => (
                    <ToggleActionButton
                      key='carousel-block-toggle-button'
                      inner={props}
                    />
                  ),
                ]
              : undefined
          }
          classNames={{
            children: 'md:text-left',
          }}
        >
          <Carousel
            config={{
              ...swiperConfig,
              loop: selectedCards.length > 4,
            }}
            items={selectedCards}
            renderItem={renderCarouselItem}
            classNames={{ root: '[&_.swiper-slide]:w-[238px]' }}
          />
        </BlockHeaderContent>
      </div>
    </BlockWrapper>
  );
};
